import { Check, Copy, Loader2, Mail, RotateCcw, Send } from 'lucide-react';
import SignatureCanvas from '@/components/SignatureCanvas';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MAX_SIGNATURE_RECIPIENTS } from '@/lib/signatureRecipients';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { ContactOption } from '@/hooks/useQuotePublishing';

interface PublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: 0 | 1;
  loading: boolean;
  isPublished: boolean;
  expiryDate: string;
  onExpiryDateChange: (value: string) => void;
  publishLocale: 'nl' | 'en';
  onPublishLocaleChange: (value: 'nl' | 'en') => void;
  hasSignaturePage: boolean;
  uboSigners: { id: string; name: string; email: string }[];
  selectedUboSigner: string;
  onUboSignerChange: (id: string) => void;
  /** De ingelogde gebruiker is de gekozen UBO-tekenaar en kan dus direct tekenen. */
  canSignOnPublish: boolean;
  signOnPublish: boolean;
  onSignOnPublishChange: (value: boolean) => void;
  senderSignature: string | null;
  onSenderSignatureChange: (dataUrl: string | null) => void;
  /** Naam van de UBO-tekenaar zodra die heeft getekend. */
  senderSignedName: string | null;
  contactOptions: ContactOption[];
  selectedContactIds: string[];
  onContactToggle: (id: string) => void;
  signEmail: string;
  onSignEmailChange: (value: string) => void;
  signName: string;
  onSignNameChange: (value: string) => void;
  shareUrl: string;
  onCopyLink: () => void;
  /** Alleen binnen de HubSpot-kaart: sluit de editor, de kaart opent dan het e-mailvenster. */
  onSendViaHubSpot?: () => void;
  canWithdraw: boolean;
  withdrawLoading: boolean;
  onWithdraw: () => void;
  onConfirm: () => void;
  onClose: () => void;
}

export default function PublishDialog({
  open,
  onOpenChange,
  step,
  loading,
  isPublished,
  expiryDate,
  onExpiryDateChange,
  publishLocale,
  onPublishLocaleChange,
  hasSignaturePage,
  uboSigners,
  selectedUboSigner,
  onUboSignerChange,
  canSignOnPublish,
  signOnPublish,
  onSignOnPublishChange,
  senderSignature,
  onSenderSignatureChange,
  senderSignedName,
  contactOptions,
  selectedContactIds,
  onContactToggle,
  signEmail,
  onSignEmailChange,
  signName,
  onSignNameChange,
  shareUrl,
  onCopyLink,
  onSendViaHubSpot,
  canWithdraw,
  withdrawLoading,
  onWithdraw,
  onConfirm,
  onClose,
}: PublishDialogProps) {
  const selectedSignerName = uboSigners.find((s) => s.id === selectedUboSigner)?.name;
  const senderSignatureMissing = canSignOnPublish && signOnPublish && !senderSignature;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {step === 0 ? (
          <>
            <DialogHeader>
              <DialogTitle>Offerte publiceren</DialogTitle>
              <DialogDescription>
                Stel een verloopdatum in en publiceer de offerte. Na deze datum is de offerte niet meer beschikbaar.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Verloopdatum</Label>
                <Input
                  id="expiryDate"
                  type="date"
                  value={expiryDate}
                  onChange={(e) => onExpiryDateChange(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground">
                  Na deze datum is de offerte niet meer beschikbaar voor de klant.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="publicLocale">Taal publieke pagina</Label>
                <Select
                  value={publishLocale}
                  onValueChange={(v) => onPublishLocaleChange(v as 'nl' | 'en')}
                  disabled={loading}
                >
                  <SelectTrigger id="publicLocale">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nl">Nederlands</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Knoppen en teksten op de link voor de klant (niet de inhoud van de offerte).
                </p>
              </div>

              {hasSignaturePage && (
                <>
                  <div className="space-y-2">
                    <Label>Tekenbevoegde UBO</Label>
                    <Select
                      value={selectedUboSigner || undefined}
                      onValueChange={onUboSignerChange}
                      disabled={loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer tekenbevoegde UBO" />
                      </SelectTrigger>
                      <SelectContent>
                        {uboSigners.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {canSignOnPublish ? (
                      <div className="space-y-2 rounded-md border p-3">
                        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                          <Checkbox
                            checked={signOnPublish}
                            onCheckedChange={(checked) => onSignOnPublishChange(checked === true)}
                            disabled={loading}
                          />
                          Direct ondertekenen bij verzenden
                        </label>
                        {signOnPublish ? (
                          <>
                            <SignatureCanvas height={120} onSignatureChange={onSenderSignatureChange} disabled={loading} />
                            <p className="text-xs text-muted-foreground">
                              Je handtekening staat meteen op de offerte. Na de klant hoeft niemand meer mede te
                              ondertekenen.
                            </p>
                          </>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            Je krijgt een mail om mede te ondertekenen zodra de klant heeft getekend.
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {selectedSignerName
                          ? `${selectedSignerName} krijgt een mail om mede te ondertekenen zodra de klant heeft getekend. Direct bij verzenden tekenen kan alleen ${selectedSignerName} zelf.`
                          : 'Deze persoon wordt als tekenbevoegde namens UBO vermeld op de offerte.'}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Tekenbevoegden klant</Label>
                    {contactOptions.length > 0 ? (
                      <div className="max-h-52 space-y-1 overflow-y-auto rounded-md border p-1">
                        {contactOptions.map((c) => {
                          const checked = selectedContactIds.includes(c.id);
                          const atLimit = !checked && selectedContactIds.length >= MAX_SIGNATURE_RECIPIENTS;
                          return (
                            <label
                              key={c.id}
                              className={`flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted ${
                                atLimit ? 'cursor-not-allowed opacity-50' : ''
                              }`}
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => onContactToggle(c.id)}
                                disabled={loading || atLimit}
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium">{c.name}</span>
                                <span className="block truncate text-xs text-muted-foreground">{c.email}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Input
                          type="email"
                          value={signEmail}
                          onChange={(e) => onSignEmailChange(e.target.value)}
                          disabled={loading}
                          placeholder="klant@bedrijf.nl"
                        />
                        <Input
                          value={signName}
                          onChange={(e) => onSignNameChange(e.target.value)}
                          disabled={loading}
                          placeholder="Naam tekenbevoegde klant"
                        />
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {contactOptions.length > 0
                        ? `Kies tot ${MAX_SIGNATURE_RECIPIENTS} tekenbevoegden. Iedereen tekent via dezelfde offertelink; de offerte is pas geaccepteerd als ze allemaal hebben getekend.`
                        : 'Deze persoon wordt als tekenbevoegde namens de klant vermeld op de offerte.'}
                    </p>
                  </div>
                </>
              )}
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={onClose} disabled={loading}>
                Annuleren
              </Button>
              <Button
                onClick={onConfirm}
                disabled={loading || senderSignatureMissing}
                title={senderSignatureMissing ? 'Zet eerst je handtekening' : undefined}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    Publiceren...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-1.5" />
                    {canSignOnPublish && signOnPublish ? 'Ondertekenen en publiceren' : 'Publiceren'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{isPublished ? 'Offerte gepubliceerd' : 'Offerte publiceren'}</DialogTitle>
              <DialogDescription>
                {isPublished
                  ? 'Je offerte is gepubliceerd. Deel de link met de klant.'
                  : 'Je offerte wordt opgeslagen en gepubliceerd...'}
              </DialogDescription>
            </DialogHeader>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="space-y-4 mt-2">
                  <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2.5 text-sm text-green-700 flex items-center gap-2">
                    <Check className="h-4 w-4 shrink-0" />
                    {senderSignedName
                      ? `Offerte is door ${senderSignedName} ondertekend en beschikbaar via onderstaande link.`
                      : 'Offerte is gepubliceerd en beschikbaar via onderstaande link.'}
                  </div>
                  <div className="space-y-2">
                    <Label>Deelbare link</Label>
                    <div className="flex items-center gap-2">
                      <Input readOnly value={shareUrl} className="font-mono text-sm" />
                      <Button onClick={onCopyLink} className="shrink-0" variant="outline">
                        <Copy className="h-4 w-4" />
                        Kopieer
                      </Button>
                    </div>
                  </div>
                  {onSendViaHubSpot && (
                    <p className="text-xs text-muted-foreground">
                      Verstuur je de link via HubSpot, dan zie je bij de deal wanneer de klant de mail opent en op
                      de link klikt.
                    </p>
                  )}
                </div>
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  {canWithdraw && (
                    <Button
                      variant="destructive"
                      onClick={onWithdraw}
                      disabled={withdrawLoading}
                      className="sm:mr-auto"
                    >
                      <RotateCcw className="h-4 w-4 mr-1.5" />
                      Intrekken
                    </Button>
                  )}
                  <Button variant="outline" onClick={onClose}>
                    Sluiten
                  </Button>
                  {onSendViaHubSpot && (
                    <Button onClick={onSendViaHubSpot}>
                      <Mail className="h-4 w-4 mr-1.5" />
                      Mailen via HubSpot
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
